import React from 'react';

import { EntertainmentPlayerName } from '@/components/entertainment/EntertainmentPlayerName';
import { PaymentForm } from '@/components/entertainment/PaymentForm';
import { SharedCostForm } from '@/components/entertainment/SharedCostForm';
import { SharedCostReview } from '@/components/entertainment/SharedCostReview';
import { Modal } from '@/components/ui';
import type { EntertainmentBalance, SharedCostDraft } from '@/lib/entertainment/ledger-types';
import { submitSharedCost } from '@/lib/entertainment/ledger-service';
import type { EntertainmentCategory, User } from '@/lib/types';
import { formatCurrency } from '@/lib/utils';

type Props = {
  balances: EntertainmentBalance[];
  users: User[];
  currentUserId?: string | null;
  canManage?: boolean;
  categories?: EntertainmentCategory[];
  eligibleUsers?: User[];
  onCreatePayment?: (body: { payer_id: string; payee_id: string; amount_cents: number }) => void | Promise<void>;
  onSharedCostSubmitted?: () => void | Promise<void>;
};

function signedCurrency(cents: number): string {
  const sign = cents > 0 ? '+' : cents < 0 ? '-' : '';
  return `${sign}${formatCurrency(Math.abs(cents) / 100)}`;
}

export function EntertainmentDashboard({
  balances,
  users,
  currentUserId,
  canManage = false,
  categories = [],
  eligibleUsers = [],
  onCreatePayment,
  onSharedCostSubmitted,
}: Props) {
  const [expanded, setExpanded] = React.useState(false);
  const [activeModal, setActiveModal] = React.useState<'expense' | 'payment' | null>(null);
  const [sharedCostDraft, setSharedCostDraft] = React.useState<SharedCostDraft | null>(null);
  const rows = [...balances].sort(
    (left, right) => Math.abs(right.official_balance_cents) - Math.abs(left.official_balance_cents),
  );
  const userById = new Map(users.map((user) => [user.id, user]));
  const currentBalance = currentUserId
    ? balances.find((balance) => balance.user_id === currentUserId)
    : null;
  const myBalanceCents = currentBalance?.official_balance_cents ?? 0;

  return (
    <div className="flex flex-col gap-2">
      {/* Hero summary — always visible */}
      <div className="flex items-center gap-3 rounded-[1rem] bg-gradient-to-br from-aura-primary/[0.08] to-aura-primary/[0.04] border border-aura-primary/10 p-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-aura-primary to-aura-primary-dim text-on-primary text-xl" aria-hidden="true">
          ⚖
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-black uppercase tracking-[0.15em] text-aura-primary/60">My Balance</div>
          <div className={`text-[22px] font-black tracking-tight ${myBalanceCents >= 0 ? 'text-aura-primary' : 'text-status-error'}`}>
            {signedCurrency(myBalanceCents)}
          </div>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setActiveModal('expense')}
          className="flex-1 rounded-2xl bg-gradient-to-br from-[#008a6d] to-[#006855] px-3 py-2 text-[11px] font-extrabold uppercase tracking-wider text-white shadow-[0_2px_6px_rgba(0,104,85,0.25)] transition-all hover:shadow-[0_4px_10px_rgba(0,104,85,0.3)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aura-primary/35"
        >
          Add Expense
        </button>
        <button
          type="button"
          onClick={() => setActiveModal('payment')}
          className="flex-1 rounded-2xl border border-[#006855] bg-white px-3 py-2 text-[11px] font-extrabold uppercase tracking-wider text-[#006855] transition-colors hover:bg-[#e8f5f0] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aura-primary/30"
        >
          Make Payment
        </button>
      </div>

      {/* Toggle */}
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="flex w-full items-center justify-between rounded-xl bg-aura-primary/[0.04] border border-aura-primary/10 px-4 py-2.5 text-[13px] font-bold uppercase tracking-[0.1em] text-aura-primary transition-colors hover:bg-aura-primary/[0.07] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aura-primary/30"
        aria-expanded={expanded}
        aria-controls="entertainment-dashboard-detail"
      >
        <span>Group Dashboard</span>
        <span className={`transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`} aria-hidden="true">▼</span>
      </button>

      {/* Collapsible detail */}
      <div
        id="entertainment-dashboard-detail"
        className={`overflow-hidden transition-[max-height,opacity] duration-300 ease-out ${
          expanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="mt-3 flex flex-col gap-2">
          {rows.map((balance) => {
            const user = userById.get(balance.user_id);
            const positive = balance.official_balance_cents >= 0;
            return (
              <div
                key={balance.user_id}
                data-testid="entertainment-balance-row"
                className="flex items-center justify-between gap-3 rounded-xl border border-border-subtle border-l-[3px] border-l-aura-primary/20 bg-surface p-3 transition-colors hover:bg-aura-primary/[0.03]"
              >
                <div className="min-w-0">
                  <EntertainmentPlayerName user={user} fallbackId={balance.user_id} />
                  <div className="mt-1 text-xs text-text-muted">
                    Projected: {signedCurrency(balance.projected_balance_cents)}
                  </div>
                </div>
                <div className={`text-sm font-bold ${positive ? 'text-status-success' : 'text-status-error'}`}>
                  {signedCurrency(balance.official_balance_cents)}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modals */}
      <Modal
        isOpen={activeModal === 'expense'}
        onClose={() => {
          setActiveModal(null);
          setSharedCostDraft(null);
        }}
        title="Add Expense"
      >
        {currentUserId && (
          sharedCostDraft ? (
            <SharedCostReview
              draft={sharedCostDraft}
              users={eligibleUsers}
              categoryName={
                categories.find((c) => c.id === sharedCostDraft.category_id)?.name
                ?? sharedCostDraft.category_id
              }
              onBack={() => setSharedCostDraft(null)}
              onConfirm={async () => {
                if (!sharedCostDraft) return;
                await submitSharedCost(sharedCostDraft);
                setSharedCostDraft(null);
                setActiveModal(null);
                await onSharedCostSubmitted?.();
              }}
            />
          ) : (
            <SharedCostForm
              categories={categories}
              eligibleUsers={eligibleUsers}
              currentUserId={currentUserId}
              defaultCategoryId={categories[0]?.id}
              canChoosePayer={canManage}
              onReview={setSharedCostDraft}
            />
          )
        )}
      </Modal>

      <Modal
        isOpen={activeModal === 'payment'}
        onClose={() => setActiveModal(null)}
        title="Make Payment"
      >
        {currentUserId && onCreatePayment && (
          <PaymentForm
            currentUserId={currentUserId}
            users={users}
            balances={balances}
            canChoosePayer={canManage}
            onCreatePayment={async (body) => {
              await onCreatePayment(body);
              setActiveModal(null);
            }}
          />
        )}
      </Modal>
    </div>
  );
}
